import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';

const { FiShield, FiAlertTriangle, FiCheck, FiX } = FiIcons;

export default function ConflictCheckModal({ isOpen, onClose, assignment, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const handleSubmit = async (status) => {
    if (status === 'cleared' && !confirmed) {
      alert('Du må bekrefte at du er habil før du kan fortsette.');
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase
        .from('utreder_assignments')
        .update({
          conflict_check_status: status,
          conflict_check_notes: notes,
          conflict_check_date: new Date().toISOString(),
          status: status === 'cleared' ? 'accepted' : 'rejected'
        })
        .eq('id', assignment.id);

      if (error) throw error;

      const { data: report } = await supabase
        .from('reports')
        .select('history')
        .eq('id', assignment.report_id)
        .single();

      const newHistory = [
        ...(report?.history || []),
        {
          date: new Date().toISOString(),
          action: status === 'cleared' ? 'Habilitet bekreftet' : 'Inhabilitet meldt',
          user: 'Utreder',
          details: status === 'cleared' 
            ? 'Utreder har bekreftet sin habilitet i saken.' 
            : 'Utreder har meldt seg inhabil og saken må tildeles på nytt.'
        }
      ];

      await supabase
        .from('reports')
        .update({ history: newHistory })
        .eq('id', assignment.report_id);

      onComplete();
      onClose();
    } catch (err) {
      alert('Feil ved lagring: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[100] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-[32px] w-full max-w-xl overflow-hidden shadow-2xl border border-gray-100 dark:border-gray-800">
        <div className="p-8 border-b dark:border-gray-800 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiShield} size={24} />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Habilitetsvurdering</h2>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-0.5">Obligatorisk sjekk før oppdrag</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl text-gray-400 transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="p-8 space-y-6">
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 p-5 rounded-2xl flex gap-4">
            <SafeIcon icon={FiAlertTriangle} className="text-amber-500 shrink-0 mt-1" size={20} />
            <div className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed font-medium">
              Som profesjonell utreder er du forpliktet til å vurdere din egen habilitet i saken. 
              Du må ikke ha personlige, økonomiske eller andre interesser som kan påvirke din upartiskhet.
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-700">
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Saksdetaljer for vurdering</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Sak:</span>
                  <span className="font-bold text-gray-900 dark:text-white">{assignment.reports?.title}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Oppdragsgiver:</span>
                  <span className="font-bold text-gray-900 dark:text-white">{assignment.company_name}</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">Notater til vurderingen</label>
              <textarea
                className="w-full bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-2xl p-4 text-sm text-gray-900 dark:text-white min-h-[100px] focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                placeholder="Beskriv eventuelle koblinger eller bekreft fravær av konflikt..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer group">
              <input 
                type="checkbox" 
                className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <span className="text-xs text-gray-600 dark:text-gray-400 font-bold leading-tight group-hover:text-gray-900 dark:group-hover:text-white transition-colors uppercase tracking-tight">
                Jeg bekrefter herved at jeg har vurdert min habilitet og funnet meg skikket til å gjennomføre oppdraget upartisk og uavhengig.
              </span>
            </label>
          </div>
        </div>

        <div className="p-8 border-t dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex gap-3">
          <button
            onClick={() => handleSubmit('conflict_found')}
            disabled={loading}
            className="flex-1 px-6 py-3.5 rounded-2xl border-2 border-red-100 text-red-600 font-black text-xs uppercase tracking-widest hover:bg-red-50 transition-all flex items-center justify-center gap-2"
          >
            Meld inhabilitet
          </button>
          <button
            onClick={() => handleSubmit('cleared')}
            disabled={loading || !confirmed}
            className={`flex-[2] px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg ${
              !confirmed || loading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-500/20 active:scale-[0.98]'
            }`}
          >
            {loading ? 'Lagrer...' : 'Bekreft Habilitet'}
          </button>
        </div>
      </div>
    </div>
  );
}